import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Check if notifications are supported
    const supported = 'Notification' in window && 'serviceWorker' in navigator;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      
      // Register service worker
      registerServiceWorker();
    }
  }, []);

  const registerServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service Worker registered:', registration);
      
      // Check for existing subscription
      const existingSub = await registration.pushManager.getSubscription();
      if (existingSub) {
        setSubscription(existingSub);
      }
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  };

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        toast.success('Notifications enabled!');
        return true;
      } else if (result === 'denied') {
        toast.error('Notification permission denied');
        return false;
      }
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error('Failed to enable notifications');
      return false;
    }
  }, [isSupported]);

  const sendLocalNotification = useCallback((title, options = {}) => {
    if (permission !== 'granted') {
      console.log('Notifications not permitted');
      return;
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [100, 50, 100],
      ...options
    };

    try {
      new Notification(title, defaultOptions);
    } catch (error) {
      // Fallback for mobile - use service worker
      navigator.serviceWorker.ready.then(registration => {
        registration.showNotification(title, defaultOptions);
      });
    }
  }, [permission]);

  // Notification types for the app
  const notifyNewCatch = useCallback((username, fishType, weight) => {
    sendLocalNotification('New Catch Report! 🎣', {
      body: `${username} caught a ${weight}lb ${fishType}`,
      tag: 'new-catch',
      data: { url: '/community' }
    });
  }, [sendLocalNotification]);

  const notifyNewMessage = useCallback((senderName) => {
    sendLocalNotification('New Message 💬', {
      body: `${senderName} sent you a message`,
      tag: 'new-message',
      data: { url: '/messages' }
    });
  }, [sendLocalNotification]);

  const notifyOrderUpdate = useCallback((orderId, status) => {
    sendLocalNotification('Order Update 📦', {
      body: `Order ${orderId} is now ${status}`,
      tag: 'order-update',
      data: { url: '/my-orders' }
    });
  }, [sendLocalNotification]);

  const notifyNewFollower = useCallback((username) => {
    sendLocalNotification('New Follower! 👤', {
      body: `${username} started following you`,
      tag: 'new-follower',
      data: { url: '/community' }
    });
  }, [sendLocalNotification]);

  const value = {
    permission,
    subscription,
    isSupported,
    requestPermission,
    sendLocalNotification,
    notifyNewCatch,
    notifyNewMessage,
    notifyOrderUpdate,
    notifyNewFollower
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
